import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Waves, Globe, Calendar, MapPin, Users } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { tourApi } from '../services/tourApi';
import { TourStop } from '../data/tourStops';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const [tourStops, setTourStops] = useState<TourStop[]>([]);
  const [loading, setLoading] = useState(true);
  const featuredStop = tourStops.find(stop => stop.status === 'featured');
  const location = useLocation();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [stopsData] = await Promise.all([
          tourApi.getTourStops(),
          tourApi.getFeaturedEvent()
        ]);
        setTourStops(stopsData);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const navItems = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.africaSurfTour'), path: '/africa-surf-tour' },
    { name: t('nav.federations'), path: '/federations' },
    { name: t('nav.talents'), path: '/talents' },
    { name: 'LiveStreaming', path: '/live' },
    { name: t('nav.news'), path: '/news' },
    { name: t('nav.gallery'), path: '/gallery' },
    { name: t('nav.partners'), path: '/partners' },
    { name: t('nav.contact'), path: '/contact' }
  ];

  // Define the next language in the cycle
  const nextLanguage = () => {
    if (language === 'fr') return 'en';
    if (language === 'en') return 'pt';
    return 'fr';
  };

  // Language names for mobile menu
  const languageOptions = {
    fr: 'Français',
    en: 'English',
    pt: 'Português'
  };

  return (
    <>
      {/* Featured Event Banner */}
      {featuredStop && (
        <div className="bg-gradient-to-r from-african-500 to-sand-500 text-white py-2 px-4 text-center relative z-50">
          <div className="container mx-auto flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <Calendar className="h-4 w-4" />
                <span className="font-semibold">{t('nav.event')}</span>
              </div>
              <div className="flex items-center space-x-1">
                <MapPin className="h-3 w-3" />
                <span>{featuredStop?.location ?? 'Inconnu'}, {featuredStop?.country ?? 'Inconnu'}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Users className="h-3 w-3" />
                <span>{featuredStop?.participants ?? 0} {t('nav.surfer')}</span>
              </div>
            </div>
            <Link
              to="/news/11"
              className="bg-white text-african-600 hover:bg-gray-100 px-4 py-1 rounded-full font-semibold transition-colors duration-300 text-xs"
            >
              {t('nav.learnmore')}
            </Link>
          </div>
        </div>
      )}

      {/* Fixed Header */}
      <nav className="w-full z-50 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <div className="flex flex-col">
                <img src="/logo.png" className="w-44" alt="ASCSURF LOGO" />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`font-medium transition-all duration-300 hover:scale-105 relative group ${
                    location.pathname === item.path
                      ? 'text-orange-400'
                      : 'text-white hover:text-orange-400'
                  }`}
                >
                  {item.name}
                  <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-current transition-all duration-300 group-hover:w-full ${
                    location.pathname === item.path ? 'w-full' : ''
                  }`}></span>
                </Link>
              ))}
              
              {/* Language Toggle */}
              <button
                onClick={() => setLanguage(nextLanguage())}
                className="flex items-center space-x-1 p-2 rounded-lg text-white hover:bg-white/10 transition-all duration-300"
              >
                <Globe className="h-4 w-4" />
                <span className="text-sm font-medium">{language.toUpperCase()}</span>
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-lg text-white hover:bg-white/10 transition-colors duration-300"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="lg:hidden bg-white/95 backdrop-blur-md border-t border-gray-200">
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block px-3 py-2 text-base font-medium rounded-lg transition-colors duration-300 ${
                      location.pathname === item.path
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                <div className="px-3 py-2 space-y-1">
                  {Object.entries(languageOptions).filter(([lang]) => lang !== language).map(([lang, name]) => (
                    <button
                      key={lang}
                      onClick={() => {
                        setLanguage(lang as 'fr' | 'en' | 'pt');
                        setIsOpen(false);
                      }}
                      className="flex items-center space-x-2 px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg w-full"
                    >
                      <Globe className="h-4 w-4" />
                      <span>{name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navigation;